import React, { useState, useCallback } from 'react'
import {
    Box,
    Paper,
    Container,
    Typography,
    Button,
    Chip,
    Avatar,
    useTheme,
    useMediaQuery,
    IconButton,
    Stack
} from '@mui/material'
import { CalendarToday, ChevronLeft, ChevronRight } from '@mui/icons-material'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import Particles from 'react-tsparticles'
import { loadSlim } from 'tsparticles-slim'
import 'slick-carousel/slick/slick-theme.css'
import { useNavigate } from 'react-router-dom'
import { skillsetCourse } from '../context/SkillsetCourseData'
import Membership from './Section/Membership'
import HeroCarousel from './Section/HeroCarousel'
import Collaborators from './Section/Collaborator'
import { useLanguage } from '../context/LanguageContext'
import CurrentStatus from './Section/WhyChooseUs'

const Home = () => {
    const theme = useTheme()
    const { t } = useLanguage()
    const darkOrange = '#E65100'
    const lightOrange = '#FFAB40'
    const navigate = useNavigate()
    const primaryColor = '#FF6D00'
    const secondaryColor = '#FF9100'
    const [sliderRef, setSliderRef] = useState(null)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const heroItems = t('heroItems')

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine)
    }, [])

    // Custom arrow components for the carousel
    const SampleNextArrow = (props) => {
        const { onClick } = props
        return (
            <IconButton
                onClick={onClick}
                sx={{
                    position: 'absolute',
                    right: { xs: -12, sm: -20, md: -30 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: theme.shadows[4],
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                <ChevronRight sx={{ color: primaryColor, fontSize: 32 }} />
            </IconButton>
        )
    }

    const SamplePrevArrow = (props) => {
        const { onClick } = props
        return (
            <IconButton
                onClick={onClick}
                sx={{
                    position: 'absolute',
                    left: { xs: -12, sm: -20, md: -30 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: theme.shadows[4],
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.3s ease',
                }}
            >
                <ChevronLeft sx={{ color: primaryColor, fontSize: 32 }} />
            </IconButton>
        )
    }

    // Carousel settings
    const carouselSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: isMobile ? (isSmallMobile ? 1 : 2) : 3,
        slidesToScroll: isMobile ? (isSmallMobile ? 1 : 2) : 3,
        initialSlide: 0,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: theme.breakpoints.values.lg,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 3
                }
            },
            {
                breakpoint: theme.breakpoints.values.md,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 2
                }
            },
            {
                breakpoint: theme.breakpoints.values.sm,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    }

    return (
        <Box sx={{ overflowX: 'hidden', position: 'relative' }}>
            {/* Hero Carousel - No particles here */}
            <Box sx={{
                width: '100vw',
                // height: `calc(90vh - ${isMobile ? '250px' : '16px'})`,
                position: 'relative',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
                backgroundColor: theme.palette.background.default,
                zIndex: 2 // Above particles
            }}>
                <HeroCarousel items={heroItems} isMobile={isMobile} />
            </Box>

            {/* Main Content */}
            <Box sx={{
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Pricing Section */}
                <Box sx={{
                    pt: isMobile ? 8 : 12,
                    backgroundColor: theme.palette.background.default
                }}>
                    <Membership />
                </Box>

                {/* Upcoming Courses - Premium Section */}
                <Box sx={{
                    width: '100vw',
                    position: 'relative',
                    left: '50%',
                    right: '50%',
                    marginLeft: '-50vw',
                    marginRight: '-50vw',
                    background: `
                        linear-gradient(135deg, 
                        rgba(255, 237, 225, 0.9) 0%, 
                        rgba(255, 248, 242, 0.95) 50%,
                        rgba(255, 255, 255, 0.98) 100%)
                    `,
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `
                            radial-gradient(circle at 75% 30%, ${lightOrange}10 0%, transparent 25%),
                            radial-gradient(circle at 25% 70%, ${secondaryColor}10 0%, transparent 25%)
                        `,
                        zIndex: 0
                    }
                }}>
                    {/* Particles for this section only */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 0
                    }}>
                        <Particles
                            id="tsparticles-upcoming"
                            init={particlesInit}
                            options={{
                                fpsLimit: 60,
                                particles: {
                                    color: { value: [primaryColor, secondaryColor] },
                                    links: {
                                        color: secondaryColor,
                                        distance: 120,
                                        enable: true,
                                        opacity: 0.2,
                                        width: 1
                                    },
                                    move: {
                                        direction: "none",
                                        enable: true,
                                        outMode: "bounce",
                                        speed: 0.5,
                                        straight: false
                                    },
                                    number: {
                                        density: { enable: true, value_area: 700 },
                                        value: 50
                                    },
                                    opacity: { value: 0.3 },
                                    shape: { type: "circle" },
                                    size: {
                                        random: true,
                                        value: { min: 1, max: 4 }
                                    }
                                },
                                detectRetina: true
                            }}
                        />
                    </Box>

                    <Box sx={{
                        textAlign: 'center',
                        backgroundColor: 'white',
                        mb: isMobile ? 4 : 8,
                    }}>
                        <Collaborators t={t} />
                    </Box>

                    <Box sx={{
                        textAlign: 'center',
                        backgroundColor: 'white',
                        mb: isMobile ? 4 : 8,
                    }}>
                        <CurrentStatus t={t} />
                    </Box>

                    <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
                        <Box sx={{
                            textAlign: 'center',
                            mb: isMobile ? 4 : 8,
                            mt: isMobile ? 0 : 4
                        }}>
                            <Typography variant="h4" sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                mb: 2
                            }}>
                                {t('upcomingCourses')}
                            </Typography>
                        </Box>

                        {/* Premium Course Carousel */}
                        <Box sx={{
                            position: 'relative',
                            px: { xs: 2, md: 0 }
                        }}>
                            <Box sx={{
                                position: 'relative',
                                padding: { xs: '0 8px', sm: '0 24px', md: '0 32px' }
                            }}>
                                <Slider ref={setSliderRef} {...carouselSettings}>
                                    {skillsetCourse.map((course) => (
                                        <Box key={course.id} sx={{
                                            p: 2,
                                            outline: 'none'
                                        }}>
                                            <Paper elevation={0} sx={{
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
                                                height: '100%',
                                                background: 'rgba(255, 255, 255, 0.95)',
                                                backdropFilter: 'blur(8px)',
                                                border: '1px solid rgba(255, 109, 0, 0.1)',
                                                boxShadow: '0 8px 32px rgba(255, 109, 0, 0.08)',
                                                '&:hover': {
                                                    transform: 'translateY(-10px)',
                                                    boxShadow: '0 12px 40px rgba(255, 109, 0, 0.15)'
                                                }
                                            }}>
                                                <Box sx={{ position: 'relative' }}>
                                                    <Box
                                                        component="img"
                                                        src={course.image}
                                                        alt={course.title}
                                                        sx={{
                                                            width: '100%',
                                                            height: isMobile ? 180 : 220,
                                                            objectFit: 'cover',
                                                            filter: 'brightness(0.95)'
                                                        }}
                                                    />
                                                    <Chip
                                                        label={course.category}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 16,
                                                            right: 16,
                                                            backgroundColor: 'rgba(255,255,255,0.95)',
                                                            color: primaryColor,
                                                            fontWeight: 700,
                                                            letterSpacing: 0.5,
                                                            boxShadow: 1
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ p: 3 }}>
                                                    <Typography variant="h6" sx={{
                                                        fontWeight: 700,
                                                        mb: 1.5,
                                                        color: 'text.primary',
                                                        minHeight: isMobile ? 'auto' : '64px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {course.title}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{
                                                        mb: 2,
                                                        color: 'text.secondary',
                                                        minHeight: isMobile ? 'auto' : '72px',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {course.description}
                                                    </Typography>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mb: 3,
                                                        pt: 1,
                                                        borderTop: '1px solid rgba(255, 109, 0, 0.1)'
                                                    }}>
                                                        <Avatar
                                                            src={course.instructor.avatar}
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                mr: 1.5,
                                                                border: `2px solid ${primaryColor}`
                                                            }}
                                                        />
                                                        <Box>
                                                            <Typography variant="subtitle2">
                                                                {course.instructor.name}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{
                                                                color: 'text.secondary',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                mt: 0.2
                                                            }}>
                                                                <CalendarToday sx={{
                                                                    fontSize: 14,
                                                                    mr: 0.5
                                                                }} />
                                                                {course.date}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        sx={{
                                                            mt: 1,
                                                            py: 1.5,
                                                            borderRadius: 2,
                                                            fontWeight: 600,
                                                            background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
                                                            '&:hover': {
                                                                background: `linear-gradient(45deg, ${darkOrange}, ${primaryColor})`,
                                                                boxShadow: `0 4px 12px ${primaryColor}40`
                                                            },
                                                            color: "white"
                                                        }}
                                                        onClick={() => navigate('/skillset-course')}
                                                    >
                                                        {t('bookNow')}
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    ))}
                                </Slider>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </Box>
    )
}

export default Home